import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

import styles from "./index.module.scss";

/**
 * sealy MattressFilterListPage (PLP) body assembly (#33585). The page's
 * StreamField is EXACTLY the ``Toolbox``
 * (``MattressFilterListPageBodyContent`` adds nothing), so the shared map
 * dispatches every key. The hero section (plain model fields) renders
 * ahead of the blocks; the trailing store locator renders in the
 * full-document MattressFilterListPageTemplate after this body.
 *
 * WRAPPER PARITY (load-bearing): the legacy template rendered
 * ``{{ self.body }}`` -- the whole-field render -- so Wagtail's
 * ``StreamBlock.render_basic`` wrapped every top-level block in
 * ``<div class="w-block-{type} block-{type}">``. One wrapper per top-level
 * block is reproduced here, classes verbatim (the Generic/Basic assembly
 * pattern; contrast the Product/Reviews/Utility assemblies, whose legacy
 * templates used bare ``{% for %}`` loops and therefore render
 * ``React.Fragment``).
 */
export const MattressFilterListPageBody = (
    props: Immutable<{
        page: templates.MattressFilterListPageTemplate["page"];
        hero: templates.MattressFilterListPageTemplate["hero"];
    }>,
): React.JSX.Element => {
    // `body` is non-null (the model field is `blank=True` without
    // `null=True`); an empty stream renders no blocks. The hero wrapper
    // always renders (empty when `hero` is null).
    return (
        <>
            <div className={styles.productGridPageHero}>
                {props.hero && (
                    <>
                        <img
                            src={props.hero.image_url}
                            width={props.hero.image_width}
                            height={props.hero.image_height}
                            alt={props.hero.image_alt}
                            fetchPriority="high"
                        />
                        {/* The hero's inner <div> is class-less; its children
                            (h1 + raw description + optional CTA) arrive as one
                            server-built HTML string -- see
                            MattressFilterListPageHero in server/cms/templates.py
                            for why they can't be separate elements here. */}
                        <div
                            dangerouslySetInnerHTML={{
                                __html: props.hero.content_html,
                            }}
                        />
                    </>
                )}
            </div>
            {props.page.body.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={toolboxComponentsMap}
                />
            ))}
        </>
    );
};
